import Sortable from "sortablejs"

// Drag-to-reorder. Any container with [data-sortable-url] becomes a sortable
// list. On drop, sends a PATCH to data-sortable-url with the ordered IDs
// pulled from each child's data-sortable-id, under the param name from
// data-sortable-param. Items use a drag handle (.sortable-handle) so the
// rest of the row remains clickable.
const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

const initSortable = (container) => {
  if (container.dataset.sortableInitialized) return
  container.dataset.sortableInitialized = "1"

  const url = container.dataset.sortableUrl
  const param = container.dataset.sortableParam || "ids"
  const handleSelector = container.dataset.sortableHandle || ".sortable-handle"

  Sortable.create(container, {
    handle: handleSelector,
    animation: 150,
    ghostClass: "sortable-ghost",
    onEnd: () => {
      const ids = Array.from(container.children)
        .map((el) => el.dataset.sortableId)
        .filter(Boolean)

      const formData = new FormData()
      ids.forEach((id) => formData.append(`${param}[]`, id))

      fetch(url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        body: formData,
        credentials: "same-origin"
      })

      // Reveal any element the server flagged as drag-dependent (e.g. a
      // "Reset order" button hidden until at least one manual reorder has
      // happened). Page reload would re-evaluate visibility from scratch.
      const revealSelector = container.dataset.sortableReveal
      if (revealSelector) {
        document.querySelectorAll(revealSelector).forEach((el) => el.hidden = false)
      }
    }
  })
}

// Drag-and-drop file picker. Wraps a hidden <input type="file"> in a drop
// zone with [data-file-drop]. Dropped files are assigned to the input so the
// form submits them normally; the visible label updates to the filename.
const initFileDrop = (zone) => {
  if (zone.dataset.fileDropInitialized) return
  zone.dataset.fileDropInitialized = "1"

  const input = zone.querySelector("[data-file-drop-input]")
  const label = zone.querySelector("[data-file-drop-label]")
  if (!input || !label) return
  const defaultText = label.textContent

  const stagedClasses = ["ring-2", "ring-brand", "bg-brand/10", "border-brand"]
  const syncStaged = () => {
    const hasFile = !!input.files?.[0]
    stagedClasses.forEach((c) => zone.classList.toggle(c, hasFile))
  }

  const updateLabel = () => {
    const file = input.files?.[0]
    label.textContent = file ? file.name : defaultText
    syncStaged()
  }

  input.addEventListener("change", updateLabel)

  zone.addEventListener("dragover", (e) => {
    e.preventDefault()
    zone.classList.add("ring-2", "ring-brand")
  })
  zone.addEventListener("dragleave", () => {
    // Only strip the transient ring if nothing is staged — otherwise the
    // staged highlight stays put so the user can see a file is ready.
    if (!input.files?.[0]) zone.classList.remove("ring-2", "ring-brand")
  })
  zone.addEventListener("drop", (e) => {
    e.preventDefault()
    if (e.dataTransfer?.files?.length) {
      input.files = e.dataTransfer.files
      updateLabel()
    } else {
      syncStaged()
    }
  })
}

// Modal confirm dialog. Forms with [data-confirm] have their submit
// intercepted; the styled dialog opens, and on OK the form re-submits with
// data-confirmed="1" set so the second pass falls through. Cancel just
// closes the dialog.
//
// Optional secondary button: a form can offer an alternative action by
// setting data-confirm-secondary-label and data-confirm-secondary-form (the
// id of another form on the page). Clicking the secondary button submits
// that other form instead — used for "Archive instead" alongside Delete.
//
// The primary button picks up data-confirm-tone="destructive" to flip red.
// Listener is registered once on the document so it survives turbo:load.
const dialogState = { pendingForm: null, secondaryFormId: null }

const initConfirmDialog = () => {
  const dialog = document.getElementById("app-confirm")
  if (!dialog || dialog.dataset.appConfirmInitialized) return
  dialog.dataset.appConfirmInitialized = "1"

  const messageEl    = dialog.querySelector("[data-confirm-message]")
  const okBtn        = dialog.querySelector("[data-confirm-ok]")
  const cancelBtn    = dialog.querySelector("[data-confirm-cancel]")
  const secondaryBtn = dialog.querySelector("[data-confirm-secondary]")

  const submit = (form) => {
    // Server-side proof that the user passed through the dialog. Destructive
    // collection actions (e.g. destroy_all) check params[:confirm] == "YES"
    // and reject otherwise, so a stray DELETE that bypasses this dialog is
    // refused at the controller. The hidden input is added on confirm and
    // reused on subsequent submits of the same form.
    if (!form.querySelector('input[name="confirm"][data-confirm-token]')) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "confirm"
      input.value = "YES"
      input.setAttribute("data-confirm-token", "1")
      form.appendChild(input)
    }
    form.dataset.confirmed = "1"
    if (typeof form.requestSubmit === "function") form.requestSubmit()
    else form.submit()
  }

  const close = (action) => {
    dialog.close()
    const form = dialogState.pendingForm
    const secondaryFormId = dialogState.secondaryFormId
    dialogState.pendingForm = null
    dialogState.secondaryFormId = null
    if (action === "ok" && form) submit(form)
    if (action === "secondary" && secondaryFormId) {
      const secondaryForm = document.getElementById(secondaryFormId)
      if (secondaryForm) submit(secondaryForm)
    }
  }

  okBtn.addEventListener("click", (e) => { e.preventDefault(); close("ok") })
  cancelBtn.addEventListener("click", (e) => { e.preventDefault(); close("cancel") })
  if (secondaryBtn) {
    secondaryBtn.addEventListener("click", (e) => { e.preventDefault(); close("secondary") })
  }
  dialog.addEventListener("cancel", (e) => { e.preventDefault(); close("cancel") })
}

const handleConfirmSubmit = (event) => {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return
  const message = form.dataset.confirm
  if (!message || form.dataset.confirmed === "1") return

  const dialog = document.getElementById("app-confirm")
  if (!dialog || typeof dialog.showModal !== "function") return // graceful fallback to native submit
  event.preventDefault()

  dialog.querySelector("[data-confirm-message]").textContent = message
  const okBtn = dialog.querySelector("[data-confirm-ok]")
  const tone = form.dataset.confirmTone === "destructive" ? "destructive" : "default"
  okBtn.dataset.tone = tone
  okBtn.textContent = form.dataset.confirmOkLabel || (tone === "destructive" ? "Delete" : "Confirm")

  const secondaryBtn = dialog.querySelector("[data-confirm-secondary]")
  if (secondaryBtn) {
    const secondaryLabel = form.dataset.confirmSecondaryLabel
    const secondaryFormId = form.dataset.confirmSecondaryForm
    if (secondaryLabel && secondaryFormId) {
      secondaryBtn.textContent = secondaryLabel
      secondaryBtn.hidden = false
      dialogState.secondaryFormId = secondaryFormId
    } else {
      secondaryBtn.hidden = true
      dialogState.secondaryFormId = null
    }
  }

  dialogState.pendingForm = form
  dialog.showModal()
}

// Decide whether the GE form needs a confirm dialog and, if so, set
// data-confirm dynamically before the generic dialog handler runs. Two
// scenarios:
//   - Transfer: ticking corresponding while another GE already holds it.
//   - Untick:   removing the corresponding flag from the only corresponding GE.
// Reads three data-* attributes set by the form template:
//   data-corresponding-form         — opts the form into this handler
//   data-existing-corresponding-name — name of the current corresponding GE
//                                      on this SI (empty if none, or if the
//                                      record being edited is itself it)
//   data-this-is-corresponding      — "1" if this is the current corresponding
const handleCorrespondingFormSubmit = (event) => {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return
  if (form.dataset.confirmed === "1") return
  if (form.dataset.correspondingForm !== "1") return

  const checkbox = form.querySelector('input[type="checkbox"][name="contribution[corresponding]"]')
  if (!checkbox) return

  const isChecked = checkbox.checked
  const existingName = form.dataset.existingCorrespondingName
  const thisIsCorresponding = form.dataset.thisIsCorresponding === "1"

  if (isChecked && existingName) {
    form.dataset.confirm = `There is already a corresponding guest editor: ${existingName}. Saving will move the corresponding role to this person. Continue?`
    form.dataset.confirmTone = "default"
    form.dataset.confirmOkLabel = "Move corresponding"
  } else if (!isChecked && thisIsCorresponding) {
    form.dataset.confirm = "Unticking this will leave the SI with no corresponding guest editor. Continue?"
    form.dataset.confirmTone = "destructive"
    form.dataset.confirmOkLabel = "Save anyway"
  }
}

document.addEventListener("submit", handleCorrespondingFormSubmit)
document.addEventListener("submit", handleConfirmSubmit)

// Inline title-collision hint for the new-SI form. The Rails view embeds
// existing SIs (per the user's access) as JSON on the form; this handler
// watches the title and journal inputs, normalises both sides with the same
// rules as SpecialIssue.normalize_title_for_match in Ruby, and surfaces a
// soft "may duplicate X" hint underneath the title field while typing.
// Submitting still works either way — the server-side guard (which prompts
// confirm_duplicate) is the hard backstop.
const normaliseSiTitle = (str) => {
  if (!str) return null
  const s = String(str)
    .toLowerCase()
    .replace(/[–—:]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^[\s\W_]+|[\s\W_]+$/g, "")
  return s.length ? s : null
}

const initSiDuplicateHint = (form) => {
  let existing
  try {
    existing = JSON.parse(form.dataset.existingSis || "[]")
  } catch {
    existing = []
  }
  const titleInput   = form.querySelector("[data-si-title-input]")
  const journalInput = form.querySelector("[data-si-journal-input]")
  const hint         = form.querySelector("[data-si-duplicate-hint-target]")
  if (!titleInput || !journalInput || !hint) return

  const refresh = () => {
    const target = normaliseSiTitle(titleInput.value)
    const journalId = String(journalInput.value || "")
    if (!target || !journalId) {
      hint.classList.add("hidden")
      hint.textContent = ""
      return
    }
    const match = existing.find((si) =>
      String(si.journal_id) === journalId &&
      normaliseSiTitle(si.title) === target
    )
    if (match) {
      hint.textContent = `An SI titled “${match.title}” already exists on this journal. You can still create another with this title.`
      hint.classList.remove("hidden")
    } else {
      hint.classList.add("hidden")
      hint.textContent = ""
    }
  }

  titleInput.addEventListener("input", refresh)
  journalInput.addEventListener("change", refresh)
  refresh()
}

// Tracker Help chat. A floating button opens a slide-in drawer that POSTs
// messages to /help_chat and renders the assistant's reply. History is held
// in the closure of the initialiser — the drawer element is data-turbo-
// permanent, so both the DOM and the closure outlive page navigations and
// the chat stays open with its scrollback intact as the user clicks around.
const initHelpChat = () => {
  const root = document.getElementById("help-chat")
  if (!root || root.dataset.helpChatInitialized) return
  root.dataset.helpChatInitialized = "1"

  const url       = root.dataset.helpChatUrl
  const panel     = root.querySelector("[data-help-chat-panel]")
  const toggle    = root.querySelector("[data-help-chat-toggle]")
  const closeBtn  = root.querySelector("[data-help-chat-close]")
  const form      = root.querySelector("[data-help-chat-form]")
  const input     = root.querySelector("[data-help-chat-input]")
  const submit    = root.querySelector("[data-help-chat-submit]")
  const messages  = root.querySelector("[data-help-chat-messages]")
  const empty     = root.querySelector("[data-help-chat-empty]")
  const errorEl   = root.querySelector("[data-help-chat-error]")
  const suggests  = root.querySelectorAll("[data-help-chat-suggestion]")

  const history = []

  // Strip leftover markdown emphasis the model occasionally emits despite the
  // plain-text instruction in the system prompt — we render with textContent,
  // so unrendered ** characters end up visible as literal asterisks.
  const stripMarkdown = (text) =>
    text.replace(/\*\*([\s\S]+?)\*\*/g, "$1").replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1$2")

  const open = () => {
    panel.hidden = false
    toggle.hidden = true
    setTimeout(() => input.focus(), 0)
  }
  const close = () => {
    panel.hidden = true
    toggle.hidden = false
  }

  toggle.addEventListener("click", open)
  closeBtn.addEventListener("click", close)
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !panel.hidden) close()
  })

  suggests.forEach((btn) => {
    btn.addEventListener("click", () => {
      input.value = btn.textContent.trim()
      form.requestSubmit()
    })
  })

  const appendBubble = (role, text) => {
    if (empty && !empty.hidden) empty.hidden = true
    const wrap = document.createElement("div")
    wrap.className = role === "user" ? "flex justify-end" : "flex justify-start"
    const bubble = document.createElement("div")
    bubble.className = role === "user"
      ? "max-w-[85%] rounded-lg bg-brand/90 text-gray-900 px-3 py-2 whitespace-pre-wrap"
      : "max-w-[85%] rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-gray-100 px-3 py-2 whitespace-pre-wrap"
    bubble.textContent = text
    wrap.appendChild(bubble)
    messages.appendChild(wrap)
    messages.scrollTop = messages.scrollHeight
    return bubble
  }

  const showError = (msg) => {
    errorEl.textContent = msg
    errorEl.hidden = false
  }
  const clearError = () => {
    errorEl.hidden = true
    errorEl.textContent = ""
  }

  const setBusy = (busy) => {
    input.disabled = busy
    submit.disabled = busy
    submit.textContent = busy ? "…" : "Send"
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault()
    const text = input.value.trim()
    if (!text) return
    clearError()

    appendBubble("user", text)
    history.push({ role: "user", content: text })
    input.value = ""

    setBusy(true)
    const placeholder = appendBubble("assistant", "…")

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({ messages: history })
      })

      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        placeholder.textContent = data.error || "Something went wrong. Please try again."
        placeholder.classList.add("text-brand-red")
        // Roll the last user turn off the history so the next try isn't tied
        // to a failed exchange the model never saw.
        history.pop()
        return
      }

      const reply = data.text || "(no response)"
      placeholder.textContent = stripMarkdown(reply)
      history.push({ role: "assistant", content: reply })
    } catch (_err) {
      placeholder.textContent = "Network error. Please try again."
      placeholder.classList.add("text-brand-red")
      history.pop()
      showError("Couldn't reach the server.")
    } finally {
      setBusy(false)
      input.focus()
    }
  })
}

// Auto-opens flash modals rendered by shared/_flash.html.erb. Two flavours
// share this handler: alerts (red, "Warning") for problems the user must see,
// and info (brand, "Done") for post-action messages that carry next-step
// instructions. Both use [data-app-flash-alert] so the same JS opens either.
// Routine "saved" notices stay inline — making every redirect modal would
// interrupt every save.
const initFlashAlertDialog = () => {
  document.querySelectorAll("[data-app-flash-alert]").forEach((dialog) => {
    if (typeof dialog.showModal !== "function") return
    if (dialog.dataset.appFlashAlertOpened) return
    dialog.dataset.appFlashAlertOpened = "1"

    const close = () => dialog.close()
    dialog.querySelector("[data-app-flash-alert-ok]")?.addEventListener("click", close)
    dialog.addEventListener("cancel", (e) => { e.preventDefault(); close() })
    dialog.showModal()
  })
}

// Auto-submit a form when a [data-autosubmit] control changes. Replaces
// inline onchange handlers, which the app-wide CSP (script-src 'self')
// blocks. Used by the journal filter on the SI index.
const initAutoSubmit = (el) => {
  if (el.dataset.autosubmitInitialized) return
  el.dataset.autosubmitInitialized = "1"
  el.addEventListener("change", () => {
    const form = el.form
    if (!form) return
    if (typeof form.requestSubmit === "function") form.requestSubmit()
    else form.submit()
  })
}

const initAll = () => {
  document.querySelectorAll("[data-sortable-url]").forEach(initSortable)
  document.querySelectorAll("[data-file-drop]").forEach(initFileDrop)
  document.querySelectorAll("[data-si-duplicate-hint]").forEach(initSiDuplicateHint)
  document.querySelectorAll("[data-autosubmit]").forEach(initAutoSubmit)
  initConfirmDialog()
  initFlashAlertDialog()
  initHelpChat()
}

document.addEventListener("DOMContentLoaded", initAll)
document.addEventListener("turbo:load", initAll)
